import { sortByName } from "../../utils/util";
import {
    DataGrid,
    GridCellEditCommitParams,
    GridCellParams,
    GridColDef,
    GridColumnHeaderParams,
    GridRowId,
    GridRowsProp,
    GridSelectionModel,
} from "@mui/x-data-grid";
import { ArrowLeft, ArrowRight, Edit3, QrCode } from "lucide-react";
import styled from "styled-components";
import {
    Action,
    Color,
    Company,
    Designer,
    Inventory,
    Manufacturer,
    Material,
    Product,
    ProductCategory,
    ProductStatus,
    QRCode,
} from "../../types";
import { SoecoButton } from "../../components/SoecoButton";
import {
    createProductReport,
    useUpdateProduct,
    createMergedProductReport,
} from "../../hooks/productHooks";
import { useNavigate } from "react-router-dom";
import { imagesUrl } from "../../services/endpoints";
import { useEffect, useMemo, useRef, useState } from "react";
import { CustomToolbar } from "../../components/CustomToolbar/CustomToolbar";
import { useSnackBar } from "../../context/SnackbarContext";
import { LoaderModal } from "../../components/Modals/LoaderModal";
import { AvatarWithFallback } from "../../components/AvatarWithFallback";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Pagination,
    PaginationItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { theme } from "../../theme";

type Props = {
    merged: boolean;
    products: Product[];
    companyId: string;
    inventoryId: string;
    handleRowClick: (id: GridRowId) => void;
    inventoryName: string;
    inventorySerialNumber: number;
    productColors: Color[];
    productActions: Action[];
    productStatuses: ProductStatus[];
    productCategories: ProductCategory[];
    productManufacturers: Manufacturer[];
    productDesigners: Designer[];
    productMaterials: Material[];
    inventories: Inventory[];
    companies: Company[];
    qrCodes: QRCode[];
};
type SearchChip = { field: string; value: string };

export const InventoryTable = (props: Props) => {
    const navigate = useNavigate();
    const mutation = useUpdateProduct();
    const snackBar = useSnackBar();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [page, setPage] = useState(parseInt(sessionStorage.getItem("inventoryPage") || "1", 10));
    const [pageSize] = useState(100);
    const [selectedRows, setSelectedRows] = useState<GridSelectionModel>([]);
    const categoryMapping: Record<string, string> = {};
    props.productCategories.forEach(cat => {
        categoryMapping[cat.name] = cat.id;
    });
    const editTitle =
        selectedRows.length > 1 ? `edit (${selectedRows.length}) products` : `edit (1) product`;
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [chips, setChips] = useState<SearchChip[]>([]);
    const handleChipsChange = (newChips: SearchChip[]) => {
        setChips(newChips);
    };

    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [sumOpen, setSumOpen] = useState(false);
    const [bulkData, setBulkData] = useState<{
        name?: string;
        roomPlacement?: string;
        warehousePlacement?: string;
        categories?: string[];
        action?: string;
    }>({});
    const rows: GridRowsProp = useMemo(() => {
        return props.products.map(product => ({
            image: product.imageUrls && product.imageUrls[0],
            companyId: product.companyId,
            inventoryId: product.inventoryId,
            id: product.id,
            name: product.name,
            categories: product.categories
                ?.map(id => props.productCategories.find(c => c.id === id)?.name)
                .filter(Boolean) as string[],
            manufacturer: product.manufacturer
                ? props.productManufacturers.find(m => m.id === product.manufacturer)?.name
                : undefined,
            designer: product.designer
                ? props.productDesigners.find(d => d.id === product.designer)?.name
                : undefined,
            color: product.color,
            measurements: product.measurements,
            co2e: product.co2e,
            status: product.status
                ? props.productStatuses.find(s => s.id === product.status)?.name
                : undefined,
            comment: product.comment,
            netPrice: product.netPrice,
            purchasePrice: product.purchasePrice,
            marketPrice: product.marketPrice,
            warehousePlacement: product.warehousePlacement,
            town: product.town,
            roomPlacement: product.roomPlacement,
            quantity: product.quantity,
            action: product.action
                ? props.productActions.find(a => a.id === product.action)?.name
                : undefined,
            colors: product.colors
                ?.map(id => props.productColors.find(c => c.id === id)?.name)
                .filter(Boolean) as string[],
            material: product.material
                ? props.productMaterials.find(m => m.id === product.material)?.name
                : undefined,
            height: product.height,
            width: product.width,
            depth: product.depth,
            length: product.length,
            seatHeight: product.seatHeight,
            qrCodeUrl: props.qrCodes.find(qr => qr.productId === product.id)?.url,
        }));
    }, [
        props.products,
        props.productCategories,
        props.productColors,
        props.productActions,
        props.productDesigners,
        props.productMaterials,
        props.productManufacturers,
        props.productStatuses,
        props.qrCodes,
    ]);
    const [filteredRows, setFilteredRows] = useState(rows);

    type RowType = {
        [key: string]: string | number | undefined;
    };
    const handleSaveClick = () => {
        setBulkEditOpen(false);
        setConfirmOpen(true);
    };

    const handleConfirm = () => {
        setConfirmOpen(false);
        handleApplyBulkEdit();
    };

    const handleCancel = () => {
        setConfirmOpen(false);
    };
    const handleCreateReport = async (columns: string[]) => {
        try {
            setIsLoading(true);

            let data: ArrayBuffer;

            if (props.merged) {
                data = await createMergedProductReport(
                    props.companyId,
                    props.inventoryId,
                    props.inventoryId,
                    columns,
                );
            } else {
                data = await createProductReport(props.companyId, props.inventoryId, columns);
            }

            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `${props.inventoryName.replace(/\s/g, "_")}_${
                    props.inventorySerialNumber ?? 0
                }.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            if (error instanceof Error) {
                snackBar.showSnackBar("Failed to generate report", "error");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleProductEdit = (id: GridRowId) => {
        navigate(`/companies/${props.companyId}/inventories/${props.inventoryId}/products/${id}`);
    };

    const handleProductQuantityChange = (params: GridCellEditCommitParams) => {
        const id = params.id as string;
        const quantity = params.value as number;

        const row = props.products.find(p => p.id === id);

        mutation.mutate(
            {
                companyId: props.companyId,
                inventoryId: props.inventoryId,
                productId: id,
                product: {
                    companyId: props.companyId,
                    inventoryId: props.inventoryId,
                    id: id,
                    quantity: quantity,
                    categories: row?.categories,
                    colors: row?.colors,
                    netPrice: row?.netPrice || undefined,
                    purchasePrice: row?.purchasePrice || undefined,
                    marketPrice: row?.marketPrice || undefined,
                    co2e: row?.co2e,
                    height: row?.height,
                    width: row?.width,
                    depth: row?.depth,
                    length: row?.length,
                    seatHeight: row?.seatHeight,
                    action: row?.action,
                    comment: row?.comment,
                    designer: row?.designer,
                    material: row?.material,
                    manufacturer: row?.manufacturer,
                    name: row?.name,
                    roomPlacement: row?.roomPlacement,
                    status: row?.status,
                    town: row?.town,
                    warehousePlacement: row?.warehousePlacement,
                    avalible: row?.avalible,
                    address: row?.address,
                },
            },
            {
                onSuccess: () => {
                    snackBar.showSnackBar("Product quantity updated successfully", "success");
                },
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                    }
                },
            },
        );
    };

    // Render accessible column header with proper ARIA labels
    // Adds descriptive labels to column menu buttons for screen readers (WCAG 4.1.2)
    const renderColumnHeader = (params: GridColumnHeaderParams) => {
        const headerName = params.colDef.headerName || params.field;
        const headerRef = useRef<HTMLElement>(null);

        useEffect(() => {
            // Find the parent element with role="columnheader" and add aria-label
            if (headerRef.current) {
                const columnHeaderEl = headerRef.current.closest('[role="columnheader"]');
                if (columnHeaderEl) {
                    columnHeaderEl.setAttribute("aria-label", headerName);

                    // Find the menu button and enhance its accessibility
                    const menuButton = columnHeaderEl.querySelector(
                        'button[aria-label="Menu"]',
                    ) as HTMLButtonElement;

                    if (menuButton) {
                        // Replace generic "Menu" with descriptive label
                        menuButton.setAttribute("aria-label", `Column options for ${headerName}`);

                        // Ensure menu button is in tab order
                        if (menuButton.tabIndex === -1) {
                            menuButton.tabIndex = 0;
                        }
                    }
                }
            }
        }, [headerName]);

        return (
            <strong
                ref={headerRef}
                tabIndex={0}
                style={{ fontWeight: "bold", outline: "none" }}
                onFocus={e => {
                    // Visual focus indicator for keyboard navigation
                    e.currentTarget.style.outline = "2px solid #d4a418";
                    e.currentTarget.style.outlineOffset = "2px";
                }}
                onBlur={e => {
                    // Remove outline when focus leaves
                    e.currentTarget.style.outline = "none";
                }}
            >
                {headerName}
            </strong>
        );
    };
    const renderColumnHeaderContent = (params: GridCellParams) => {
        return (
            <div role="gridcell" aria-label={`${params.field}`} tabIndex={0}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                    }}
                >
                    <span tabIndex={0}>{params.value}</span>
                </div>
            </div>
        );
        // },
    };

    const columns: GridColDef[] = [
        {
            field: "image",
            hideSortIcons: true,
            disableColumnMenu: true,
            headerName: "Image",
            renderHeader: renderColumnHeader,
            flex: 0.8,
            minWidth: 70,
            editable: false,
            renderCell: params => {
                const columnName = params.colDef.headerName || params.field;
                return (
                    <div
                        role="gridcell"
                        aria-label={`${columnName}: Product image`}
                        style={{ width: "100%", height: "100%" }}
                        tabIndex={0}
                    >
                        <AvatarWithFallback
                            src={imagesUrl + (params.value as string) + "small.jpg"}
                            fallbackSrc={imagesUrl + (params.value as string)}
                        />
                    </div>
                );
            },
        },
        {
            field: "name",
            headerName: "Name",
            flex: 0.9,
            editable: false,
            align: "left",
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "town",
            headerName: "Town",
            flex: 0.7,
            minWidth: 80,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "warehousePlacement",
            headerName: "Building",
            flex: 0.7,
            minWidth: 80,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "roomPlacement",
            headerName: "Room",
            flex: 0.5,
            minWidth: 60,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "quantity",
            headerName: "Qty",
            flex: 0.2,
            editable: !props.merged,
            type: "number",
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "qrCodeUrl",
            headerName: "QR",
            flex: 0.6,
            minWidth: 100,
            editable: false,
            sortable: true,
            renderHeader: renderColumnHeader,
            renderCell: params => (
                <div
                    role="gridcell"
                    aria-label={params.value ? `QR code: ${String(params.value)}` : "No QR code"}
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    {params.value ? (
                        <>
                            <QrCode
                                size={16}
                                color={theme.palette.primary.main}
                                style={{ flexShrink: 0 }}
                            />
                            <span
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {params.value as string}
                            </span>
                        </>
                    ) : (
                        <span style={{ color: theme.palette.text.disabled }}>—</span>
                    )}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Edit",
            flex: 0.2,
            minWidth: 70,
            align: "left",
            editable: false,
            sortable: false,
            hideable: false,
            renderHeader: renderColumnHeader,
            renderCell: params => {
                if (props.merged) return null;
                return (
                    <TableActions>
                        <SoecoButton
                            icon={true}
                            size="small"
                            onClick={() => handleProductEdit(params.id)}
                        >
                            <Edit3 size={24} />
                        </SoecoButton>
                    </TableActions>
                );
            },
        },
        // Default hidden columns
        {
            field: "companyId",
            headerName: "Company",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "inventoryId",
            headerName: "Inventory",
            flex: 0.2,
            renderHeader: renderColumnHeader,
            minWidth: 100,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "id",
            headerName: "Id",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "categories",
            headerName: "Categories",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "manufacturer",
            headerName: "Supplier",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "designer",
            headerName: "Designer",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "color",
            headerName: "Color",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "colors",
            headerName: "Colors",
            flex: 0.6,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "measurements",
            headerName: "Measurements",
            flex: 0.2,
            minWidth: 110,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "comment",
            headerName: "Comment",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "netPrice",
            headerName: "Net Price",
            flex: 0.2,
            minWidth: 80,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "purchasePrice",
            headerName: "Purchase Price",
            flex: 0.2,
            minWidth: 120,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "marketPrice",
            headerName: "Market Price",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "co2e",
            headerName: "CO2e",
            flex: 0.2,
            minWidth: 50,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "action",
            headerName: "Action",
            flex: 0.4,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "material",
            headerName: "Material",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "height",
            headerName: "Height (mm)",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "width",
            headerName: "Width (mm)",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "depth",
            headerName: "Depth (mm)",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "length",
            headerName: "Length (mm)",
            flex: 0.2,
            minWidth: 100,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
        {
            field: "seatHeight",
            headerName: "Seating Height (mm)",
            flex: 0.2,
            minWidth: 150,
            renderHeader: renderColumnHeader,
            renderCell: renderColumnHeaderContent,
        },
    ];

    const numberOfPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
        sessionStorage.setItem("inventoryPage", newPage.toString());
    };
    const defaultVisibilityModel: Record<string, boolean> = {
        id: false,
        companyId: false,
        inventoryId: false,
        categories: false,
        manufacturer: false,
        designer: false,
        color: false,
        measurements: false,
        co2e: false,
        status: false,
        comment: false,
        netPrice: false,
        purchasePrice: false,
        marketPrice: false,
        action: false,
        colors: false,
        name: true,
        warehousePlacement: true,
        town: true,
        roomPlacement: true,
        quantity: true,
        qrCodeUrl: true,
        image: true,
        actions: true,
        material: false,
        height: false,
        width: false,
        depth: false,
        length: false,
        seatHeight: false,
    };

    const getInitialColumnVisibility = (): Record<string, boolean> => {
        try {
            const stored = localStorage.getItem("columns");
            if (!stored) return defaultVisibilityModel;
            // const visibleFields: string[] = JSON.parse(stored);
            const visibleFields = JSON.parse(stored) as string[];

            const allFields = Object.keys(defaultVisibilityModel);

            if (Array.isArray(visibleFields)) {
                // Old format: only visible field names were saved.
                // For fields absent from the list we can't tell "user hid it" from "field
                // didn't exist yet", so fall back to the intended default visibility.
                return allFields.reduce((acc, field) => {
                    acc[field] = visibleFields.includes(field) || defaultVisibilityModel[field];
                    return acc;
                }, {} as Record<string, boolean>);
            }

            // New format (object): complete visibility map — use saved value, fall back to default.
            const storedModel = visibleFields as unknown as Record<string, boolean>;
            const model = allFields.reduce((acc, field) => {
                acc[field] =
                    field in storedModel ? storedModel[field] : defaultVisibilityModel[field];
                return acc;
            }, {} as Record<string, boolean>);
            // The edit column can't be hidden, even if an older saved model hid it.
            model.actions = true;
            return model;
        } catch (error) {
            console.warn("Could not parse saved column visibility model:", error);
            return defaultVisibilityModel;
        }
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(
        getInitialColumnVisibility(),
    );
    const handleColumnVisibilityModelChange = (model: Record<string, boolean>) => {
        setColumnVisibilityModel(model);
        localStorage.setItem(
            "visibleColumns",
            JSON.stringify(Object.keys(model).filter(key => model[key])),
        );
    };
    useEffect(() => {
        if (chips.length === 0) {
            setFilteredRows(rows);
        } else {
            setFilteredRows(
                rows.filter((row: RowType) => {
                    const categoryChips = chips.filter(c => c.field === "categories");
                    const otherChips = chips.filter(c => c.field !== "categories");

                    // AND-logik för övriga fält
                    const matchesOther = otherChips.every(chip => {
                        const value = row[chip.field];
                        return typeof value === "string"
                            ? value.toLowerCase().includes(chip.value.toLowerCase())
                            : false;
                    });

                    // OR-logik för kategori: minst en match
                    const rowCategories: string[] = Array.isArray(row.categories)
                        ? row.categories
                              .map(name => props.productCategories.find(c => c.name === name)?.id)
                              .filter((id): id is string => Boolean(id))
                        : [];

                    const matchesCategory =
                        categoryChips.length === 0 ||
                        categoryChips.some(chip => rowCategories.includes(chip.value));

                    return matchesOther && matchesCategory;
                }),
            );
        }
    }, [chips, rows, props.productCategories]);

    const handleApplyBulkEdit = () => {
        selectedRows.forEach(id => {
            if (!id) return;
            const row = props.products.find(p => p.id === id);
            if (!row) return;
            if (!row.id) return;
            mutation.mutate(
                {
                    companyId: props.companyId,
                    inventoryId: props.inventoryId,
                    productId: row.id.toString(),
                    product: {
                        ...row,
                        name: bulkData.name ?? row.name,
                        roomPlacement: bulkData.roomPlacement ?? row.roomPlacement,
                        warehousePlacement: bulkData.warehousePlacement ?? row.warehousePlacement,
                        categories: bulkData.categories ?? row.categories,
                        action: bulkData.action ?? row.action,
                        town: row?.town ?? undefined,
                        quantity: row.quantity,
                        netPrice: row?.netPrice ?? undefined,
                        purchasePrice: row?.purchasePrice ?? undefined,
                        marketPrice: row?.marketPrice ?? undefined,
                        co2e: row?.co2e ?? undefined,
                        height: row?.height ?? undefined,
                        width: row?.width ?? undefined,
                        depth: row?.depth ?? undefined,
                        length: row?.length ?? undefined,
                        seatHeight: row?.seatHeight ?? undefined,
                    },
                },
                {
                    onSuccess: () => snackBar.showSnackBar("Produkten uppdaterades", "success"),
                    onError: error => {
                        if (error instanceof Error) snackBar.showSnackBar(error.message, "error");
                    },
                },
            );
        });

        setBulkEditOpen(false);
        setSelectedRows([]);
    };
    const placeholderMap: Record<SearchChip["field"], string> = {
        name: "Name",
        roomPlacement: "Room",
        warehousePlacement: "Building",
        categories: "Categories",
        action: "Action",
    };

    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(
        sessionStorage.getItem("selectedProduct"),
    );
    const highlightedDone = useRef(false);

    useEffect(() => {
        const savedId = sessionStorage.getItem("selectedProduct");

        if (!savedId) return;

        if (
            sessionStorage.getItem("selectedPage") !== sessionStorage.getItem("inventoryPage") &&
            sessionStorage.getItem("inventoryPage")
        ) {
            return;
        }

        const scrollToRow = () => {
            const row = document.querySelector(`[data-id="${savedId}"]`);
            if (row) {
                setHighlightedRowId(savedId);
                row.scrollIntoView({ block: "center" });
                highlightedDone.current = true;

                // Remove yellow color after done
                setTimeout(() => {
                    setHighlightedRowId(null);
                }, 7000);
                sessionStorage.removeItem("selectedProduct");
                sessionStorage.removeItem("selectedPage");
                return true;
            }
            return false;
        };

        // This is needed cuz the row might not be rendered yet
        // Maximum 2 seconds
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            if (scrollToRow() || attempts > 40) {
                clearInterval(interval);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [page, filteredRows]);

    useEffect(() => {
        // HEADER
        const headerCheckbox = document.querySelector(
            '.MuiDataGrid-columnHeader input[type="checkbox"]',
        );

        if (headerCheckbox) {
            headerCheckbox.setAttribute(
                "aria-label",
                `Select all ${filteredRows.length} products in table ${props.inventoryName}`,
            );
            headerCheckbox.setAttribute("id", "select-all-checkbox");
        }

        // RADER
        const rowCheckboxes = document.querySelectorAll(
            '.MuiDataGrid-cellCheckbox input[type="checkbox"]',
        );

        rowCheckboxes.forEach((checkbox, index) => {
            const rowNumber = index + 1;
            type RowType = {
                name?: string;
            };

            const row = filteredRows[index] as RowType | undefined;
            const rowName = row?.name ?? "";
            checkbox.setAttribute("aria-label", `Select product  ${rowName} on row ${rowNumber}`);
        });
    }, [filteredRows]);

    return (
        <>
            {isLoading && <LoaderModal message="Downloading report" />}

            <DataTable
                disableVirtualization={true}
                selectionModel={selectedRows}
                checkboxSelection={props.merged ? false : true}
                onSelectionModelChange={newSelection => setSelectedRows(newSelection)}
                disableColumnFilter
                disableDensitySelector
                aria-describedby="inventory-table-description"
                aria-rowcount={filteredRows.length + 1}
                aria-colcount={
                    columns.filter(col => columnVisibilityModel[col.field] !== false).length
                }
                components={{ Toolbar: CustomToolbar }}
                componentsProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                        csvOptions: { allColumns: true, delimiter: ";" },
                        handleCreateReport: handleCreateReport,
                        chips: chips,
                        setChips: handleChipsChange,
                        productCategories: props.productCategories,
                        selectedRows: selectedRows,
                        setBulkEditOpen: setBulkEditOpen,
                        setSumOpen: setSumOpen,
                        merged: props.merged,
                        filteredRows: filteredRows,
                        allProducts: props.products,
                        inventories: props.inventories,
                        companies: props.companies,
                    },
                    pagination: {
                        count: numberOfPages,
                    },
                }}
                getRowClassName={params =>
                    params.id === highlightedRowId ? "highlighted-row" : ""
                }
                hideFooter
                rows={filteredRows}
                columns={columns}
                pageSize={pageSize}
                page={page - 1}
                rowsPerPageOptions={[100]}
                disableSelectionOnClick={false}
                onRowClick={params => {
                    props.handleRowClick(params.id);
                    sessionStorage.setItem("selectedProduct", params.id.toString());
                    sessionStorage.setItem("selectedPage", page.toString());
                }}
                onCellEditCommit={params => {
                    handleProductQuantityChange(params);
                }}
                rowSpacingType="margin"
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                columnVisibilityModel={columnVisibilityModel}
            />
            <Stack
                spacing={2}
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                marginTop={2}
            >
                <Pagination
                    shape="rounded"
                    aria-label={`Pagination, ${numberOfPages} pages`}
                    color="primary"
                    page={page}
                    onChange={handlePageChange}
                    count={numberOfPages}
                    renderItem={item => (
                        <PaginationItem
                            {...item}
                            components={{
                                previous: () => (
                                    <ArrowLeft style={{ cursor: "pointer" }} size={16} />
                                ),
                                next: () => <ArrowRight style={{ cursor: "pointer" }} size={16} />,
                            }}
                            sx={{
                                backgroundColor: item.selected ? "black" : "transparent",
                                color: item.selected ? "white" : "black",
                                "&:hover": {
                                    backgroundColor: item.selected ? "black" : "lightgrey",
                                },
                                fontFamily: "'Yllw Sans a1 241219', sans-serif",
                            }}
                        />
                    )}
                />
            </Stack>

            <Dialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)}>
                <DialogTitle>
                    {" "}
                    {editTitle.charAt(0).toUpperCase() + editTitle.slice(1).toLowerCase()}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Name"
                        value={bulkData.name || ""}
                        onChange={e => setBulkData(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth
                        margin="dense"
                    />
                    <TextField
                        label="Room"
                        value={bulkData.roomPlacement || ""}
                        onChange={e =>
                            setBulkData(prev => ({ ...prev, roomPlacement: e.target.value }))
                        }
                        fullWidth
                        margin="dense"
                    />
                    <TextField
                        label="Building"
                        value={bulkData.warehousePlacement || ""}
                        onChange={e =>
                            setBulkData(prev => ({ ...prev, warehousePlacement: e.target.value }))
                        }
                        fullWidth
                        margin="dense"
                    />

                    <FormControl fullWidth margin="dense">
                        <InputLabel>Categories</InputLabel>
                        <Select
                            multiple
                            value={bulkData.categories || []}
                            onChange={e =>
                                setBulkData(prev => ({
                                    ...prev,
                                    categories: e.target.value as string[],
                                }))
                            }
                            renderValue={selected =>
                                selected
                                    .map(id => props.productCategories.find(c => c.id === id)?.name)
                                    .filter(Boolean)
                                    .join(", ")
                            }
                        >
                            {sortByName(props.productCategories).map(c => (
                                <MenuItem key={c.id} value={c.id}>
                                    <Checkbox
                                        checked={bulkData.categories?.includes(c.id) ?? false}
                                    />
                                    <ListItemText primary={c.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Action</InputLabel>
                        <Select
                            multiple
                            value={bulkData.action ? [bulkData.action] : []}
                            onChange={e => {
                                const value = e.target.value as string[];
                                setBulkData(prev => ({
                                    ...prev,
                                    action: value.length > 0 ? value[value.length - 1] : "",
                                }));
                            }}
                            renderValue={selected =>
                                props.productActions.find(a => a.id === selected[0])?.name || ""
                            }
                        >
                            {sortByName(props.productActions)
                                .filter(c => c.name !== "Publish on public Marketplace")
                                .map(c => (
                                    <MenuItem key={c.id} value={c.id}>
                                        <Checkbox checked={bulkData.action === c.id} />
                                        <ListItemText primary={c.name} />
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkEditOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveClick} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={confirmOpen}
                onClose={handleCancel}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">Confirm Changes</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        {`Are you sure you want to ${editTitle}?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>No</Button>
                    <Button
                        sx={{
                            height: 30.35,
                            fontSize: "13px",
                            backgroundColor: `${theme.palette.secondary.main}`,
                            color: "#000",
                            "&:hover": {
                                backgroundColor: "#b18800ea",
                            },
                        }}
                        onClick={handleConfirm}
                        variant="contained"
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={sumOpen} onClose={() => setSumOpen(false)}>
                <DialogTitle>{"Total products"}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Typography sx={{}} variant="h2">
                            {filteredRows.reduce(
                                (sum: number, row) =>
                                    sum + (Number((row as RowType).quantity) || 0),
                                0,
                            )}
                        </Typography>
                    </Box>
                    {chips.length > 0 && (
                        <Box sx={{ mt: 2, width: "100%" }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Active Filters:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {chips.map((chip, index) => (
                                    <Chip
                                        key={index}
                                        label={
                                            chip.field === "categories"
                                                ? `Category: ${
                                                      props.productCategories.find(
                                                          c => c.id === chip.value,
                                                      )?.name ?? chip.value
                                                  }`
                                                : `${placeholderMap[chip.field]}: ${chip.value}`
                                        }
                                        size="small"
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSumOpen(false)} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const DataTable = styled(DataGrid)`
    width: 100%;

    .highlighted-row {
        animation: highlight 7s ease forwards;
    }

    @keyframes highlight {
        0% {
            background-color: #fed42a;
        }
        100% {
            background-color: transparent;
        }
    }

    /* Accessibility: Make column menu icons always visible and keyboard accessible (WCAG 2.1.1, 4.1.2) */
    & .MuiDataGrid-menuIcon {
        visibility: visible !important;
        width: auto !important;
        opacity: 0.6;
        transition: opacity 0.2s ease-in-out;
    }

    & .MuiDataGrid-menuIconButton {
        opacity: 0.6 !important;
        transition: opacity 0.2s ease-in-out;
    }

    /* Full opacity on hover and focus for better discoverability */
    & .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon,
    & .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-menuIcon {
        opacity: 1;
    }

    & .MuiDataGrid-menuIconButton:hover,
    & .MuiDataGrid-menuIconButton:focus {
        opacity: 1 !important;
        background-color: rgba(0, 0, 0, 0.04);
    }

    /* Ensure visible focus indicator for keyboard navigation */
    & .MuiDataGrid-menuIconButton:focus-visible {
        outline: 2px solid #d4a418;
        outline-offset: 2px;
    }

    /* Make cells keyboard-focusable and show clear focus indicator (WCAG 2.1.1, 2.4.7) */
    & .MuiDataGrid-cell {
        cursor: pointer;
    }

    & .MuiDataGrid-cell:focus,
    & .MuiDataGrid-cell:focus-within {
        outline: 2px solid #50724dff;
        outline-offset: -2px;
    }

    & .MuiDataGrid-cell[tabindex="0"] {
        outline-offset: -2px;
    }
`;

const TableActions = styled.div`
    display: flex;
    font-size: 6px;
    width: 10%;
`;
